"use client";

import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { getMobileTabRouteIndex } from "@/lib/mobileTabRoutes";

const SLIDE_PX = 28;
const TRANSITION = {
  duration: 0.22,
  ease: [0.32, 0.72, 0, 1] as const,
};

export default function MobileTabPageTransition({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname() || "/";
  const prevPathRef = useRef(pathname);
  const [prevPath, setPrevPath] = useState(pathname);
  const [mobileSlideEnabled, setMobileSlideEnabled] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setMobileSlideEnabled(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const prevIdx = getMobileTabRouteIndex(prevPath);
  const nextIdx = getMobileTabRouteIndex(pathname);

  useEffect(() => {
    setPrevPath(prevPathRef.current);
    prevPathRef.current = pathname;
  }, [pathname]);

  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");

  if (!mobileSlideEnabled || isAdminRoute) {
    return <>{children}</>;
  }

  const isTabToTab = prevIdx >= 0 && nextIdx >= 0 && prevPath !== pathname;

  const forward = nextIdx > prevIdx;
  const useSlide = isTabToTab;
  const sameRoute = prevPath === pathname;

  return (
    <div className="relative min-h-0 max-lg:overflow-x-clip">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={pathname}
          className="min-h-0 w-full"
          initial={
            sameRoute
              ? false
              : useSlide
                ? { opacity: 0, x: forward ? SLIDE_PX : -SLIDE_PX }
                : { opacity: 0 }
          }
          // Only animate `x` for tab-to-tab slides. Always setting `x: 0` here
          // leaves a transform on the layer and breaks `position: sticky` inside
          // (e.g. admin bulk selection toolbar).
          animate={useSlide ? { opacity: 1, x: 0 } : { opacity: 1 }}
          exit={
            useSlide
              ? { opacity: 0, x: forward ? -SLIDE_PX : SLIDE_PX }
              : { opacity: 0 }
          }
          transition={TRANSITION}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
